import { MigrationRunDetails, MigrationRunDetailsStatus } from '@urbanx/agx.support.client';
import dayjs from 'dayjs';

export interface MigratorRunGroup {
  id: string;
  startedAt: string;
  migrations: MigrationRunDetails[];
}

export interface GroupedMigrations {
  notRun: MigrationRunDetails[];
  runs: MigratorRunGroup[];
  previousRuns: MigrationRunDetails[];
}

const migrationName = (migration: MigrationRunDetails) =>
  migration.displayName ?? migration.title ?? '';

export const groupMigrations = (migrations: MigrationRunDetails[]): GroupedMigrations => {
  const notRun: MigrationRunDetails[] = [];
  const previousRuns: MigrationRunDetails[] = [];
  const runsById = new Map<string, MigratorRunGroup>();

  for (const migration of migrations) {
    if (migration.status === MigrationRunDetailsStatus.NotRun) {
      notRun.push(migration);
      continue;
    }

    if (!migration.migratorRunId || !migration.migratorRunStartedUtc) {
      previousRuns.push(migration);
      continue;
    }

    const run = runsById.get(migration.migratorRunId);
    if (run) {
      run.migrations.push(migration);
    } else {
      runsById.set(migration.migratorRunId, {
        id: migration.migratorRunId,
        startedAt: migration.migratorRunStartedUtc,
        migrations: [migration],
      });
    }
  }

  notRun.sort((left, right) => migrationName(left).localeCompare(migrationName(right)));
  previousRuns.sort(
    (left, right) =>
      (right.lastRunStartedUtc ? dayjs(right.lastRunStartedUtc).valueOf() : 0) -
      (left.lastRunStartedUtc ? dayjs(left.lastRunStartedUtc).valueOf() : 0)
  );

  const runs = [...runsById.values()]
    .sort((left, right) => dayjs(right.startedAt).valueOf() - dayjs(left.startedAt).valueOf())
    .map((run) => ({
      ...run,
      migrations: run.migrations.sort(
        (left, right) =>
          dayjs(left.lastRunStartedUtc).valueOf() - dayjs(right.lastRunStartedUtc).valueOf()
      ),
    }));

  return { notRun, runs, previousRuns };
};
